// קונקטור (02 §1–2).
import { z } from 'zod';
import { Id, Ts, rowBase } from './common.ts';

/**
 * איך פונים לטבלאות של חברה במקור — כתובת, **לא מדיניות**: אין היקף/רשימת טבלאות (02 §1). בינה-1 קוראת כל מה שה-login רואה;
 * הכתובת משמשת רק את ה-SQL שהפלטפורמה מייצרת (03 סנכרון, 04/05 introspection): MSSQL — schema (dbo) ובלי קידומת (DB לכל חברה);
 * Oracle — סכמה אחת וקידומת לחברה.
 */
export const SourceAddress = z.object({
  schema: z.string().nullable(),
  tablePrefix: z.string(),
});
export type SourceAddress = z.infer<typeof SourceAddress>;

/** setup (03 §6): ALTER … CHANGE_TRACKING ב-login aibify_setup, allowlist תחבירי בקונקטור. */
export const SourceJobKind = z.enum(['read', 'write', 'setup']);
export type SourceJobKind = z.infer<typeof SourceJobKind>;

/** צורת ה-wire של עבודה (GET /api/connector/jobs). */
export const SourceJob = z.object({
  id: Id,
  /** אסימון הניסיון (= attempts אחרי ה-claim); חובה ב-chunks/done — ניסיון ישן נדחה (02 §1, lease). */
  attempt: z.number().int().positive(),
  worldId: Id,
  company: z.string().nullable(),
  kind: SourceJobKind,
  sql: z.string().min(1),
  params: z.array(z.union([z.string(), z.number(), z.null()])),
  timeoutS: z.number().int().positive(),
  limits: z.object({ chunkBytes: z.number().int().positive(), maxBytes: z.number().int().positive() }),
  approvalId: Id.optional(),
});
export type SourceJob = z.infer<typeof SourceJob>;

export const SourceJobStatus = z.enum(['queued', 'running', 'done', 'error', 'guard', 'too_large', 'timeout']);
export type SourceJobStatus = z.infer<typeof SourceJobStatus>;

/** sys.source_job */
export const SourceJobRow = z.object({
  ...rowBase,
  worldId: Id,
  company: z.string().nullable(),
  connectorId: Id.nullable(),
  kind: SourceJobKind,
  sql: z.string(),
  params: z.array(z.union([z.string(), z.number(), z.null()])),
  timeoutS: z.number().int().positive(),
  limits: z.object({ chunkBytes: z.number().int().positive(), maxBytes: z.number().int().positive() }),
  approvalId: Id.nullable(),
  /** `sync` | `ai1` | `artifact:<id>` */
  requestedBy: z.string().regex(/^(sync|ai1|artifact:.+)$/),
  status: SourceJobStatus,
  claimedAt: Ts.nullable(),
  attempts: z.number().int().nonnegative(),
  finishedAt: Ts.nullable(),
  ms: z.number().int().nullable(),
  bytes: z.number().int().nullable(),
  n: z.number().int().nullable(),
  error: z.string().nullable(),
  resultRef: z.string().nullable(),
});
export type SourceJobRow = z.infer<typeof SourceJobRow>;

/** sys.source_job_chunk */
export const SourceJobChunk = z.object({
  ...rowBase,
  jobId: Id,
  seq: z.number().int().nonnegative(),
  last: z.boolean(),
  n: z.number().int().nonnegative(),
  bytes: z.number().int().nonnegative(),
  storageRef: z.string(),
});
export type SourceJobChunk = z.infer<typeof SourceJobChunk>;

export const ConnectorStatus = z.enum(['pending', 'paired', 'online', 'disconnected', 'revoked']);
export type ConnectorStatus = z.infer<typeof ConnectorStatus>;

/** sys.connector */
export const Connector = z.object({
  ...rowBase,
  name: z.string(),
  hostname: z.string().nullable(),
  tokenHash: z.string(),
  version: z.string().nullable(),
  configVersion: z.number().int().nonnegative(),
  status: ConnectorStatus,
  lastSeenAt: Ts.nullable(),
  revokedAt: Ts.nullable(),
});
export type Connector = z.infer<typeof Connector>;

/** sys.connector_pairing */
export const ConnectorPairing = z.object({
  ...rowBase,
  codeHash: z.string(),
  createdBy: Id,
  expiresAt: Ts,
  usedAt: Ts.nullable(),
  connectorId: Id.nullable(),
  secretsRef: z.string().nullable(),
});
export type ConnectorPairing = z.infer<typeof ConnectorPairing>;

export const ConnectorDriver = z.enum(['mssql', 'oracle', 'odbc']);
export type ConnectorDriver = z.infer<typeof ConnectorDriver>;

/** sys.connector_world */
export const ConnectorWorld = z.object({
  ...rowBase,
  connectorId: Id,
  worldId: Id,
  company: z.string().nullable(),
  driver: ConnectorDriver,
  sourceSchema: z.string().nullable(),
  tablePrefix: z.string(),
  readConnRef: z.string(),
  writeConnRef: z.string().nullable(),
  /** 03 §6: login ההפעלה (aibify_setup) לעבודות setup בלבד; null = אין */
  setupConnRef: z.string().nullable(),
});
export type ConnectorWorld = z.infer<typeof ConnectorWorld>;

